import { useEffect, useMemo, useState } from 'react'

import { traceService } from '../services/traceService'
import { API_BASE_URL } from '../utils/api'

type TenantOption = {
  id: string
  name: string
  slug?: string
}

type FeatureFlag = {
  id: string
  tenantId: string
  key: string
  description?: string
  enabled: boolean
  rolloutPercent: number
  createdAt: string
  updatedAt: string
}

type CreateFeatureFlagDto = {
  key: string
  description?: string
  enabled?: boolean
  rolloutPercent?: number
}

type UpdateFeatureFlagDto = {
  description?: string
  enabled?: boolean
  rolloutPercent?: number
}

const getAdminHeaders = (tenantId: string): HeadersInit => {
  const token = localStorage.getItem('monitoring_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
  }
}

async function adminRequest<T>(tenantId: string, path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...getAdminHeaders(tenantId),
      ...(options.headers || {}),
    },
  })

  if (!response.ok) {
    throw new Error(`Feature flags request failed: ${response.status}`)
  }

  return response.json()
}

const featureFlagsApi = {
  getAll: async (tenantId: string) =>
    adminRequest<{ data: FeatureFlag[] }>(tenantId, '/feature-flags').then((response) => response.data),

  create: async (tenantId: string, data: CreateFeatureFlagDto) =>
    adminRequest<{ data: FeatureFlag }>(tenantId, '/feature-flags', {
      method: 'POST',
      body: JSON.stringify(data),
    }).then((response) => response.data),

  update: async (tenantId: string, key: string, data: UpdateFeatureFlagDto) =>
    adminRequest<{ data: FeatureFlag }>(tenantId, `/feature-flags/${key}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }).then((response) => response.data),

  delete: async (tenantId: string, key: string) =>
    adminRequest(tenantId, `/feature-flags/${key}`, { method: 'DELETE' }),

  toggle: async (tenantId: string, key: string) =>
    adminRequest<{ data: FeatureFlag }>(tenantId, `/feature-flags/${key}/toggle`, {
      method: 'POST',
    }).then((response) => response.data),
}

const DEFAULT_AGENT_MODE_FLAG: FeatureFlag = {
  id: 'default-use-agent-mode',
  tenantId: '',
  key: 'use_agent_mode',
  description: 'Modo agent loop del bot. Si no existe, el backend lo trata como habilitado por defecto.',
  enabled: true,
  rolloutPercent: 100,
  createdAt: '',
  updatedAt: '',
}

export default function FeatureFlagsPage() {
  const [tenants, setTenants] = useState<TenantOption[]>([])
  const [selectedTenantId, setSelectedTenantId] = useState('')
  const [flags, setFlags] = useState<FeatureFlag[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null)
  const [formData, setFormData] = useState<CreateFeatureFlagDto>({
    key: '',
    description: '',
    enabled: false,
    rolloutPercent: 0,
  })

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === selectedTenantId) || null,
    [tenants, selectedTenantId]
  )

  const agentModeFlag = useMemo(
    () => flags.find((flag) => flag.key === 'use_agent_mode') || { ...DEFAULT_AGENT_MODE_FLAG, tenantId: selectedTenantId },
    [flags, selectedTenantId]
  )

  const fetchTenants = async () => {
    const response = await traceService.getTenants()
    setTenants(response.data)
    setSelectedTenantId((current) => current || response.data[0]?.id || '')
  }

  const fetchFlags = async (tenantId: string) => {
    if (!tenantId) {
      setFlags([])
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const data = await featureFlagsApi.getAll(tenantId)
      setFlags(data)
    } catch (err) {
      setError('Failed to load feature flags')
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        await fetchTenants()
      } catch (err) {
        setError('Failed to load tenants')
        console.error(err)
        setIsLoading(false)
      }
    }
    void load()
  }, [])

  useEffect(() => {
    if (!selectedTenantId) return
    void fetchFlags(selectedTenantId)
  }, [selectedTenantId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedTenantId) return
    try {
      if (editingFlag) {
        await featureFlagsApi.update(selectedTenantId, editingFlag.key, {
          description: formData.description,
          enabled: formData.enabled,
          rolloutPercent: formData.rolloutPercent,
        })
      } else {
        await featureFlagsApi.create(selectedTenantId, formData)
      }
      setShowModal(false)
      setEditingFlag(null)
      setFormData({ key: '', description: '', enabled: false, rolloutPercent: 0 })
      await fetchFlags(selectedTenantId)
    } catch (err) {
      setError(`Failed to ${editingFlag ? 'update' : 'create'} feature flag`)
      console.error(err)
    }
  }

  const handleDelete = async (key: string) => {
    if (!selectedTenantId) return
    if (!confirm(`Are you sure you want to delete the feature flag "${key}"?`)) return
    try {
      await featureFlagsApi.delete(selectedTenantId, key)
      await fetchFlags(selectedTenantId)
    } catch (err) {
      setError('Failed to delete feature flag')
      console.error(err)
    }
  }

  const handleToggle = async (flag: FeatureFlag) => {
    if (!selectedTenantId) return
    try {
      await featureFlagsApi.toggle(selectedTenantId, flag.key)
      await fetchFlags(selectedTenantId)
    } catch (err) {
      setError('Failed to toggle feature flag')
      console.error(err)
    }
  }

  const handleAgentModeToggle = async () => {
    if (!selectedTenantId) return
    try {
      const existing = flags.find((flag) => flag.key === 'use_agent_mode')
      if (!existing) {
        await featureFlagsApi.create(selectedTenantId, {
          key: 'use_agent_mode',
          description: DEFAULT_AGENT_MODE_FLAG.description,
          enabled: false,
          rolloutPercent: 0,
        })
      } else {
        await featureFlagsApi.update(selectedTenantId, 'use_agent_mode', {
          enabled: !existing.enabled,
          rolloutPercent: existing.enabled ? 0 : 100,
          description: existing.description || DEFAULT_AGENT_MODE_FLAG.description,
        })
      }
      await fetchFlags(selectedTenantId)
    } catch (err) {
      setError('Failed to update use_agent_mode')
      console.error(err)
    }
  }

  const openEditModal = (flag: FeatureFlag) => {
    setEditingFlag(flag)
    setFormData({
      key: flag.key,
      description: flag.description || '',
      enabled: flag.enabled,
      rolloutPercent: flag.rolloutPercent,
    })
    setShowModal(true)
  }

  const openCreateModal = () => {
    setEditingFlag(null)
    setFormData({ key: '', description: '', enabled: false, rolloutPercent: 0 })
    setShowModal(true)
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Feature Flags</h1>
          <p className="text-sm text-gray-500 mt-1">Administra flags por tenant. `use_agent_mode` es `true` por defecto si no existe.</p>
        </div>
        <button
          onClick={openCreateModal}
          disabled={!selectedTenantId}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
        >
          + New Feature Flag
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tenant</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name} · {tenant.id}
                </option>
              ))}
            </select>
          </div>

          <div className="border rounded-lg p-4 bg-slate-50">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold text-slate-900">use_agent_mode</div>
                <div className="text-sm text-slate-600">
                  {selectedTenant ? `Tenant: ${selectedTenant.name}` : 'Selecciona un tenant'}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Si no existe el flag, el backend usa `true` por defecto.
                </div>
              </div>
              <button
                onClick={handleAgentModeToggle}
                disabled={!selectedTenantId}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  agentModeFlag.enabled
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-red-100 text-red-800 hover:bg-red-200'
                } disabled:bg-gray-100 disabled:text-gray-400`}
              >
                {agentModeFlag.enabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : flags.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No feature flags configured for this tenant. `use_agent_mode` sigue activo por default hasta que lo deshabilites.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rollout</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {flags.map((flag) => (
                <tr key={flag.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm">{flag.key}</code>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{flag.description || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggle(flag)}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        flag.enabled ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      {flag.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{flag.rolloutPercent}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => openEditModal(flag)} className="text-blue-600 hover:text-blue-900 mr-4">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(flag.key)} className="text-red-600 hover:text-red-900">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">{editingFlag ? 'Edit Feature Flag' : 'Create Feature Flag'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
                <input
                  type="text"
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                  disabled={!!editingFlag}
                  required
                  pattern="^[a-zA-Z][a-zA-Z0-9_-]*$"
                  className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-100"
                  placeholder="e.g., use_agent_mode"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Optional description"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Rollout Percentage</label>
                <input
                  type="number"
                  value={formData.rolloutPercent}
                  onChange={(e) => setFormData({ ...formData, rolloutPercent: parseInt(e.target.value, 10) || 0 })}
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={!!formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Enabled</span>
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  {editingFlag ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
